import { XMarkIcon as CloseIcon, AdjustmentsHorizontalIcon as FiltersIcon } from '@heroicons/react/24/outline';
import { useMemo, useState } from 'react';
// import { AdjustmentsHorizontalIcon as FiltersIcon } from '@heroicons/react/24/solid';
import Accordion from 'components/commercetools-ui/atoms/accordion';
import Drawer from 'components/commercetools-ui/atoms/drawer';
import { useFormat } from 'helpers/hooks/useFormat';
import { Category } from 'shared/types/product';
import { useProductList } from '../../context';
import useDynamicFacets from '../../hooks/useDynamicFacets';
import CategoryMenu from '../category-menu';
import SortFacet from '../facets/sort';
import CertECardNav from '../sidebar/cert-ecard-nav';
import CertPhysicalCardNav from '../sidebar/cert-physical-card-nav';

export interface MobileFacetsProps {
  categories: Category[];
  activeCategory: Category;
  activeParentCategory: Category;
}

const MobileFacets: React.FC<MobileFacetsProps> = ({ categories, activeCategory, activeParentCategory }) => {
  const { formatMessage: formatProductMessage } = useFormat({ name: 'product' });

  const { removeAllRefinements, facetsConfiguration, totalItems } = useProductList();

  const [isOpen, setIsOpen] = useState(false);

  const accordionClassNames = useMemo(
    () => ({
      root: 'py-20 px-16',
      icon: 'stroke-secondary-black w-20 stroke-2',
      border: 'border-b border-neutral-400',
      button: 'text-14',
    }),
    [],
  );

  const facets = useDynamicFacets({
    configuration: facetsConfiguration,
    render: ({ attribute, Component }) => (
      <Accordion
        key={attribute}
        className={`${accordionClassNames.root} ${accordionClassNames.border}`}
        buttonClassName={accordionClassNames.button}
        closedSectionTitle={facetsConfiguration[attribute].label}
        variant="arrow"
        iconClassName={accordionClassNames.icon}
      >
        <div className="px-16 py-28">{Component}</div>
      </Accordion>
    ),
  });

  const sortFacet = useMemo(
    () => (
      <Accordion
        className={accordionClassNames.root}
        buttonClassName={accordionClassNames.button}
        closedSectionTitle={formatProductMessage({ id: 'sortBy', defaultMessage: 'Sory by' })}
        variant="arrow"
        iconClassName={accordionClassNames.icon}
      >
        <div className="px-16 py-28">
          <SortFacet {...{ categories }} />
        </div>
      </Accordion>
    ),
    [formatProductMessage, accordionClassNames],
  );

  function loadSidebar(categoryKey: string | null | undefined) {
    switch (categoryKey) {
      case 'certification-card-physical':
      case 'certification-card-type-physical':
      case 'certification-card-special-card-physical':
        return <CertPhysicalCardNav categories={categories} activeCategory={activeCategory ?? {}} />;
      case 'certification-card':
      case 'certification-card-ecard':
      case 'certification-card-type-ecard':
      case 'certification-card-special-card-ecard':
        return <CertECardNav categories={categories} activeCategory={activeCategory ?? {}} />;
      default:
        return <CategoryMenu categories={categories} activeCategory={activeCategory ?? {}} />;
    }
  }

  const categoryMenu = useMemo(
    () => (
      <div className="px-16 py-28">
        <CategoryMenu categories={categories} activeCategory={activeCategory} />
      </div>
    ),
    [categories, activeCategory, accordionClassNames],
  );

  return (
    <>
      <div className="flex items-center justify-between border-b border-neutral-400 pb-16 pt-16 lg:hidden">
        <div>
          <button
            className="flex min-w-[80px] cursor-pointer items-center gap-8 rounded-md border border-transparent bg-white py-6 pr-12 text-14 leading-[20px] transition hover:border-gray-500"
            onClick={() => setIsOpen(true)}
          >
            <span className="text-14">
              {formatProductMessage({ id: 'sortAndFilter', defaultMessage: 'Filter & Sort' })}
            </span>
            <FiltersIcon className="mt-2 w-16 stroke-padi-gray-darkest" />
          </button>
        </div>
        <div className="flex items-center gap-16">
          <span className="text-14 text-secondary-black">
            {totalItems} {formatProductMessage({ id: 'items', defaultMessage: 'Items' })}
          </span>
        </div>
      </div>
      <Drawer
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        direction="left"
        className="w-[90%] max-w-[400px] bg-white"
      >
        <div className="flex items-center justify-between border-b border-neutral-400 px-12 py-16">
          <h3 className="text-18">{formatProductMessage({ id: 'sortAndFilter', defaultMessage: 'Filter & Sort' })}</h3>
          <CloseIcon className="w-24 stroke-secondary-black" onClick={() => setIsOpen(false)} />
        </div>
        <div className="grow overflow-auto">
          {facets}
          {sortFacet}
          <div className="px-16 py-28">{loadSidebar(activeParentCategory?.key ?? activeCategory?.key)}</div>
        </div>
        <div className="w-full bg-white py-18">
          <div className="border-t border-neutral-400 py-12 text-center text-14 text-secondary-black">
            {totalItems} {formatProductMessage({ id: 'products', defaultMessage: 'Products' })}
          </div>
          <div className="flex items-center gap-18 px-14">
            <div className="w-full overflow-hidden rounded-md border border-transparent transition hover:border-primary-black">
              <button
                onClick={() => {
                  removeAllRefinements();
                  setIsOpen(false);
                }}
                className="w-full cursor-pointer border border-primary-black py-6 text-14 font-medium"
              >
                {formatProductMessage({ id: 'clear.all', defaultMessage: 'Clear All' })}
              </button>
            </div>
            <button
              className="w-full rounded-md bg-primary-black py-8 text-14 font-medium text-white transition hover:bg-gray-500"
              onClick={() => setIsOpen(false)}
            >
              {formatProductMessage({ id: 'done', defaultMessage: 'Done' })}
            </button>
          </div>
        </div>
      </Drawer>
    </>
  );
};

export default MobileFacets;
